/**
 * Multi-tenant API scoping helper (added 2026-05-19 to fix the Store
 * Mirror data leak — see App.js).
 *
 * Single source of truth for the `store_id` query param that EVERY API call
 * silently carries. Two distinct values feed it:
 *
 *   1. The signed-in user's `home_store_id` (set right after login). This
 *      makes regular crew at non-3094 stores automatically see their own
 *      store's data instead of falling through to 3094 (which would happen
 *      if we let the backend's `_sid()` default run).
 *
 *   2. The admin's mirror override (`viewingStoreId` in App.js). When the
 *      admin opens a sister store from the District Admin console, this
 *      takes precedence so the whole app reconfigures to that store's data
 *      for the duration of the mirror session.
 *
 * Why a module-level variable + axios interceptor instead of threading
 * the param through every fetch call site (HomePage, InboxPage, Settings,
 * etc.)? There are 150+ axios calls across the frontend; touching every
 * one would be a 2-day refactor AND a regression magnet. An interceptor
 * fixes all of them in one place.
 *
 * Security note: this is a CONVENIENCE param, not an access-control
 * boundary. The backend still verifies admin/DM permissions on writes
 * via per-endpoint `actor_id` lookups. A malicious crew member who
 * tampers with the param would only ever leak READ access to a sister
 * store's lean roster — no PINs, no auth secrets, no destructive ops.
 */
import axios from "axios";

// Mutable module-level state. Updated by App.js via setApiStoreId() on
// login/logout/mirror-toggle. Empty string ⇒ no override (the backend
// falls back to STORE_3094_ID, which is the legacy single-store behavior
// we keep for callers that haven't been migrated yet).
let _storeId = "";
// 2026-08-29 — actor_id state, mirrored from the signed-in user so
// the axios interceptor can auto-attach it to every API call. This
// closes the Watchdog `UNAUTH 2xx on /api/xxx` leaks on business
// endpoints (tally, biz, wtd-summary, crew-performance, messages, …)
// without touching every fetch call site. Empty when logged out; the
// gated endpoints will 401 which is exactly what we want.
let _actorId = "";

export const setApiStoreId = (id) => {
  _storeId = (id || "").trim();
  // Also publish on `window` so React render code can read the active
  // scope synchronously without going through context. Used by the
  // ManagerDashboard "Adjust Crew Emails" client-side filter as a belt +
  // braces guard against showing out-of-store crew if a fetch raced the
  // mirror toggle. (Added 2026-05-22.)
  try {
    if (typeof window !== "undefined") window.__wowActiveStoreId = _storeId;
  } catch { /* ignore — non-browser env */ }
};

export const setApiActorId = (id) => {
  _actorId = (id || "").trim();
  try {
    if (typeof window !== "undefined") window.__wowActorId = _actorId;
  } catch { /* ignore — non-browser env */ }
};

export const getApiStoreId = () => _storeId;
export const getApiActorId = () => _actorId;

// Install ONCE at module import. We mutate `config.params` so the param
// appears on the URL whether the caller used axios.get(url) or
// axios.get(url, {params}) — both code paths route through this hook.
//
// IMPORTANT: if the caller has ALREADY passed an explicit `store_id`
// (e.g., the StoreDetailPanel fetching the roster for store Lakewood
// while admin is mirroring Cherry Knolls), we leave it untouched. Only
// inject when no store_id was specified — otherwise the global mirror
// state would clobber per-call scoping requests and we'd ship a "ghost
// roster" bug where every store shows the admin's mirror-store crew.
axios.interceptors.request.use((config) => {
  // Always attach the LP session cookie (SEC-001 fix 2026-02). The
  // cookie is HttpOnly + scoped to `/api/security` so it's only sent
  // on those requests anyway — enabling withCredentials globally is
  // safe and avoids per-call opt-in that a fresh feature might forget.
  config.withCredentials = true;
  // CSRF header for cookie-authenticated writes. Attaching it on every
  // request is harmless (endpoints without CSRF checks ignore it) and
  // avoids a per-endpoint opt-in that a new feature might forget. The
  // browser's Fetch spec forbids cross-origin pages from setting this
  // without a CORS preflight, which our CORS config declines — so a
  // hostile page can't forge a state-changing LP call even with a
  // stolen cookie.
  config.headers = { ...(config.headers || {}), "X-Requested-With": "XMLHttpRequest" };
  // ── actor_id injection (2026-08-29) ──────────────────────────────
  // Auto-attach the signed-in user's crew_id as `actor_id` on every
  // request so auth-gated business endpoints don't need per-call
  // opt-in. Callers that already pass actor_id explicitly (e.g. the
  // React admin scoped fetches) win — we never clobber. Also skip
  // when the URL already carries an `actor_id=` in its query string.
  if (_actorId) {
    const existingParams = config.params || {};
    const hasActorParam = existingParams && Object.prototype.hasOwnProperty.call(existingParams, "actor_id");
    let urlHasActor = false;
    try {
      const url = config.url || "";
      const qIdx = url.indexOf("?");
      if (qIdx !== -1) urlHasActor = /[?&]actor_id=/.test(url.slice(qIdx));
    } catch { /* fall through */ }
    if (!hasActorParam && !urlHasActor) {
      config.params = { ...existingParams, actor_id: _actorId };
    }
  }
  if (!_storeId) return config;
  const existing = config.params || {};
  const hasExplicit = existing && Object.prototype.hasOwnProperty.call(existing, "store_id");
  if (hasExplicit) return config;
  // Defense-in-depth (2026-05-25): some callers (ReportsPage tabs, a few
  // legacy fetches) build their query string INTO the URL directly with
  // URLSearchParams instead of going through `config.params`. The check
  // above wouldn't see those, so the interceptor would happily inject a
  // SECOND `store_id` — producing URLs like `?store_id=3015&store_id=3094`
  // where the LAST one wins and silently overrides the caller's intent.
  // This was the root cause of "Reports filter by store still shows 3094":
  // the user clicked 3015, the URL became `...?store_id=store_3015...`,
  // and the interceptor stamped `store_id=store_3094` (Dom's home) on top.
  // Now we sniff the existing URL too and skip injection if one's already
  // baked in.
  try {
    const url = config.url || "";
    const qIdx = url.indexOf("?");
    if (qIdx !== -1 && /[?&]store_id=/.test(url.slice(qIdx))) {
      return config;
    }
  } catch { /* malformed URL → fall through to normal injection */ }
  config.params = { ...existing, store_id: _storeId };
  return config;
});

/**
 * Cloudflare HTML / proxy fallback guard (2026-06-15).
 *
 * Sometimes Cloudflare returns an HTML challenge / 5xx fallback page
 * instead of the JSON our endpoints normally produce. axios would
 * happily resolve those with `response.data` being a string of HTML,
 * which then crashes downstream `.map()` / `.length` / property reads
 * and surfaces as random "App is broken" / "Cloudflare popup" reports.
 *
 * This interceptor detects HTML responses on what was supposed to be a
 * JSON endpoint and converts them into a rejection so individual call
 * sites' existing try/catch + retry logic kicks in cleanly. The error
 * carries a recognizable `code === "CLOUDFLARE_HTML"` so the future
 * global error handler can show a single friendly toast instead of one
 * per failing endpoint. (User report 2026-06-15 "we keep randomly
 * getting a Cloudflare message/popup while using the app".)
 */
axios.interceptors.response.use(
  (response) => {
    try {
      const ct = (response?.headers?.["content-type"] || "").toLowerCase();
      const body = response?.data;
      const looksLikeHtml = (
        (typeof body === "string" && body.trim().startsWith("<")) ||
        (ct.includes("text/html") && typeof body === "string")
      );
      if (looksLikeHtml) {
        const err = new Error("Upstream returned HTML — likely a Cloudflare / proxy fallback page.");
        err.code = "CLOUDFLARE_HTML";
        err.response = response;
        return Promise.reject(err);
      }
    } catch { /* shouldn't throw; fall through */ }
    return response;
  },
  (error) => {
    // Normalize FastAPI/Pydantic v2 validation errors so ANY caller doing
    // `err.response?.data?.detail` (or passing it to toast.error) always
    // gets a string, never a raw {type, loc, msg, input, url} object list.
    // Rendering the raw object as a React child threw minified error #31
    // on prod (Watchdog WD-403, 2026-08-31). We patch in-place so all 130+
    // call sites work without touching each one. (User 2026-09-01 push to
    // clean up prod React crashes.)
    try {
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const readable = detail
          .map((d) => {
            if (typeof d === "string") return d;
            if (d && typeof d === "object") {
              const msg = d.msg || d.message || "";
              const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
              return loc ? `${loc}: ${msg}` : msg;
            }
            return "";
          })
          .filter(Boolean)
          .join("; ");
        if (readable) error.response.data.detail = readable;
        else error.response.data.detail = "Request was rejected by the server.";
      } else if (detail && typeof detail === "object") {
        const msg = detail.msg || detail.message || JSON.stringify(detail);
        error.response.data.detail = typeof msg === "string" ? msg : "Request was rejected.";
      }
    } catch { /* leave the error as-is */ }
    return Promise.reject(error);
  },
);
